import DOMPurify from 'isomorphic-dompurify';
import { useState } from 'react';
import type { IntlShape } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Button,
  TextField,
  Typography,
  useDeviceType,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Accordion,
  useI18n,
  useSnackbar,
} from '@packages/shared';
import { ArrowMiniDown32 } from '@packages/themes/icons';
import type { CustomerDataWithStatus } from '@packages/utilities';
import { useConfig, useCookies, useCustomer } from '@packages/utilities';
import type { GTMEventGlycerinDisplayNewsletterConfirm } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import type { Language } from '@packages/config';
import config from '@packages/config';
import { ActivityOverlay } from '../ActivityOverlay';
import { basicContainer } from '../styles';
import { InputIDs } from '../types';
import { subscribeSheetMessages } from './helpers/messages';
import { useNlSheetTexts } from './helpers/useNlSheetTexts';
import { submitMessage } from '../GeneralForm/helpers/handleSubmit';
import { formMessages } from '../messages';
import { useFormDeviceType } from '../GeneralForm/helpers/useFormDeviceType';
import { useSubscribeNewsletterGqlMutation } from '../helpers/useSubscribeNewsletterGqlMutation';

type InputId = 'email';
type FormValues = {
  [InputIDs.EMAIL]: string;
};
type SetErrorType = `root.${InputId}` | 'root';

const newsletterSubscribeFormSchema = (formatMessage: IntlShape['formatMessage']) =>
  yup.object().shape({
    [InputIDs.EMAIL]: yup
      .string()
      .email(formatMessage(formMessages[InputIDs.EMAIL].error.valid))
      .required(formatMessage(formMessages[InputIDs.EMAIL].error)),
  });

const TermsAccordion = () => {
  const intl = useIntl();
  const { language, defaultLanguage } = useI18n();
  const { nlTexts } = useNlSheetTexts();
  const {
    host: { domain },
    forms: { newsletterSubscribe },
  } = config;
  const configLanguage = (language ?? defaultLanguage) as Language;

  return (
    <Accordion
      elevation={0}
      sx={{
        '&::before': { display: 'none' },
        typography: 'body2',
        backgroundColor: 'transparent',
      }}
    >
      <AccordionSummary
        expandIcon={<ArrowMiniDown32 />}
        aria-controls="newslettersheet-terms"
        id="newslettersheet-terms"
        sx={{
          padding: 0,
          justifyContent: 'left',
          '&.Mui-expanded': { minHeight: 'unset' },
          '.MuiAccordionSummary-content': { flexGrow: 'unset', margin: 0 },
        }}
      >
        {(nlTexts && nlTexts.field_voucher_conditions_id?.label) ||
          intl.formatMessage(subscribeSheetMessages.termsHeadline)}
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <Box sx={{ mt: 1, mb: 2, typography: 'body2', a: { color: 'text.dark' } }}>
          {nlTexts && nlTexts.field_voucher_conditions_id?.field_conditions?.processed ? (
            <span
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  nlTexts.field_voucher_conditions_id?.field_conditions.processed,
                ),
              }}
            />
          ) : (
            <FormattedMessage
              id={subscribeSheetMessages.termsContent.id}
              defaultMessage={subscribeSheetMessages.termsContent.defaultMessage}
              values={{
                aDomain: (chunks) => <a href={`https://${domain}`}>{chunks}</a>,
                aVoucher: (chunks) => (
                  <a href={newsletterSubscribe[configLanguage].voucherLink}>{chunks}</a>
                ),
              }}
            />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
const getDefaultValues = (customer: CustomerDataWithStatus | undefined): FormValues => ({
  [`${InputIDs.EMAIL}`]: customer?.email || '',
});
/**
 * A compact Newsletter Subscribe Form with a simple success page after successful submit
 */
const Page1Form = ({ onSuccess }: { onSuccess: () => void }) => {
  const { formatMessage } = useIntl();
  const { language, defaultLanguage } = useI18n();
  const { nlTexts } = useNlSheetTexts();
  const { customer } = useCustomer();
  const dispatchGTMEvent = useTracking();
  const { isMobile } = useDeviceType();
  const {
    forms: { newsletterSubscribe },
  } = config;
  const scheme = newsletterSubscribeFormSchema(formatMessage);
  const { handleSubmit, register, formState, setError, getValues } = useForm<FormValues>({
    resolver: yupResolver(scheme),
    mode: 'onTouched',
    defaultValues: getDefaultValues(customer),
  });
  const { errors, isValid, isSubmitting } = formState;
  const formDeviceType = useFormDeviceType();
  const { getCookies } = useCookies();
  const {
    forms: { useOptimizely },
  } = useConfig();
  const { addToast } = useSnackbar();
  const doSubscribeNewsletterGql = useSubscribeNewsletterGqlMutation();

  const onSubmit = (data: FormValues) =>
    getCookies()['inspire.optimizely'] === 'true' || useOptimizely
      ? doSubscribeNewsletterGql({
          formData: {
            email: data.email,
          },
          areaKey: 'slideinPromotionFooter',
        })
          .then(({ data: resultData, error: resultError }) => {
            if (resultError) {
              throw new Error(resultError.message);
            }
            if (
              resultData?.subscribeNewsletterRecipient &&
              'errors' in resultData.subscribeNewsletterRecipient &&
              resultData.subscribeNewsletterRecipient.errors &&
              resultData.subscribeNewsletterRecipient.errors.length > 0
            ) {
              const errorMessage = resultData.subscribeNewsletterRecipient.errors
                .filter(Boolean)
                .map((error) => {
                  if (error && (!error.includes('{') || !error.includes('}'))) {
                    return error;
                  }
                  const jsonPart = error?.replace(/\n|\r/g, '').match(/{.*}/)?.[0];

                  return jsonPart ? JSON.parse(jsonPart).errorMessage : error;
                })
                .join(', ');

              throw new Error(errorMessage);
            }
            onSuccess();
            dispatchGTMEvent({
              event: 'ga_event',
              eventCategory: 'User Interactions',
              eventLabel: 'newsletter',
              eventAction: 'subscribe',
            });
            if (getValues('email')) {
              dispatchGTMEvent<GTMEventGlycerinDisplayNewsletterConfirm>({
                event: 'DisplayNewsletterConfirm',
                DisplayNewsletterConfirmData: {
                  email: getValues('email'),
                  custom: { areaKey: 'slideinPromotionFooter' },
                },
              });
            }
          })
          .catch((error) => addToast({ message: error.message, severity: 'error', duration: 2500 }))
      : submitMessage<SetErrorType>({
          endpoint: 'subscribe',
          formatMessage,
          language,
          formDeviceType,
          onSuccess: () => {
            onSuccess();
            dispatchGTMEvent({
              event: 'ga_event',
              eventCategory: 'User Interactions',
              eventLabel: 'newsletter',
              eventAction: 'subscribe',
            });
            if (getValues('email')) {
              dispatchGTMEvent<GTMEventGlycerinDisplayNewsletterConfirm>({
                event: 'DisplayNewsletterConfirm',
                DisplayNewsletterConfirmData: {
                  email: getValues('email'),
                  custom: { areaKey: 'slideinPromotionFooter' },
                },
              });
            }
          },
          onError: (message) => addToast({ message, severity: 'error', duration: 2500 }),
          setError,
          convertedFormNLData: {
            emailAddress: data.email,
            areaKey: 'slideinPromotionFooter',
          },
        });

  /**
   * Gather the basic props (that only depend on the id) for each input
   */
  const getFieldProps = (inputId: InputId) => ({
    ...register(inputId),
    id: inputId,
    helperText: errors[inputId]?.message,
    error: !!errors[inputId],
  });

  const buttonText =
    (nlTexts && (isMobile ? nlTexts.field_button_label_mobile : nlTexts.field_button_label)) ||
    formatMessage(isMobile ? subscribeSheetMessages.buttonMobile : subscribeSheetMessages.button);

  const configLanguage = (language ?? defaultLanguage) as Language;
  const unsubscribeLink = newsletterSubscribe[configLanguage]?.unsubscribeLink;

  return (
    <ActivityOverlay showLoader={isSubmitting}>
      <Box sx={{ ...basicContainer, padding: 2 }}>
        <Typography variant="h5" component="p" sx={{ marginBottom: 2.25 }}>
          {(nlTexts && nlTexts.field_headline) ||
            formatMessage(subscribeSheetMessages.formHeadline)}
        </Typography>

        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Grid container columns={[1, 2]} columnSpacing={1.5}>
            <Grid item xs={1}>
              <TextField
                {...getFieldProps(InputIDs.EMAIL)}
                sx={{ paddingTop: 0 }}
                fullWidth
                placeholder={formatMessage(subscribeSheetMessages.emailPlaceholder)}
              />
            </Grid>
            <Grid item xs={1}>
              <Button
                disabled={!isValid}
                size="large"
                fullWidth
                color="primary"
                type="submit"
                clickTrackingProps={{
                  category: 'newsletter-sheet',
                  label: 'submitbutton',
                }}
              >
                {buttonText}
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ mt: 1, mb: 2, typography: 'body2', a: { color: 'text.dark' } }}>
            <FormattedMessage
              id={subscribeSheetMessages.formDescription.id}
              defaultMessage={subscribeSheetMessages.formDescription.defaultMessage}
              values={{
                a: (chunks) => <a href={unsubscribeLink}>{chunks}</a>,
              }}
            />
          </Box>

          <TermsAccordion />
        </form>
      </Box>
    </ActivityOverlay>
  );
};

export const NewsletterSubscribeCompact = () => {
  const intl = useIntl();
  const [page, setPage] = useState(1);

  return page === 1 ? (
    <Page1Form onSuccess={() => setPage(2)} />
  ) : (
    <Box sx={{ ...basicContainer, padding: 2 }}>
      <Typography variant="h3" component="p" sx={{ marginBottom: 2.25 }}>
        {intl.formatMessage(subscribeSheetMessages.successHeadline)}
      </Typography>
      <Box sx={{ paddingBottom: 2.25, typography: 'body2' }}>
        {intl.formatMessage(subscribeSheetMessages.successContent)}
      </Box>
    </Box>
  );
};
